import {
  getEbitdaAdjustentsInRespectOfTheInterestAllowanceAlternativeCalculation,
  getEbitdaAdjustmentsConsolidatedPartnership,
  getEbitdaAdjustmentsNonConsolidated,
  getEbitdaAdjustmentsQualifyingInfrastructureCompanies,
  getEbitdaCapitalDisposalsAdjustment,
  getEbitdaCapitalFairValueMovementAdjustment,
  getEbitdaDepreciationAndAmortisationAdjustment,
  getEbitdaOther,
  getEbitdaProfitBeforeTax,
} from "../../../pages/group-data/utils/ebitdaUtil";
import {
  getPageTooltips,
  getSelectedPeriod,
} from "../../../services/GroupContext";
import {
  addGroupDataSubheaderCells,
  extractKeyValuePairsFromExcel,
  getWorksheetFromFile,
} from "./DataImportUtils";
import {
  addGroupDataWorksheet,
  downloadGroupDataImportTemplate,
  getGroupDataInitialSheetBuilder,
  validateImportedGroupData,
} from "./groupDataImportUtils";

const getTableRows = (rows, isImportTemplate = true) => {
  const ebitda = getSelectedPeriod()?.ebitda;
  const result = [];
  Object.entries(rows).forEach(([key, config]) => {
    addGroupDataSubheaderCells({
      list: result,
      data: ebitda,
      key,
      config,
      isImportTemplate,
    });
  });
  return result;
};

const generateSheetBuilder = (isImportTemplate) => {
  const tooltips = getPageTooltips("ebitdaModal");
  const ebitdaProfitBeforeTax = getEbitdaProfitBeforeTax(tooltips);
  const ebitdaDepreciationAndAmortisationAdjustment =
    getEbitdaDepreciationAndAmortisationAdjustment(tooltips);
  const ebitdaCapitalFairValueMovementAdjustment =
    getEbitdaCapitalFairValueMovementAdjustment(tooltips);
  const ebitdaCapitalDisposalsAdjustment =
    getEbitdaCapitalDisposalsAdjustment(tooltips);
  const ebitdaAdjustentsInRespectOfTheInterestAllowanceAlternativeCalculation =
    getEbitdaAdjustentsInRespectOfTheInterestAllowanceAlternativeCalculation(
      tooltips,
    );
  const ebitdaAdjustmentsNonConsolidated =
    getEbitdaAdjustmentsNonConsolidated(tooltips);
  const ebitdaAdjustmentsConsolidatedPartnership =
    getEbitdaAdjustmentsConsolidatedPartnership(tooltips);
  const ebitdaAdjustmentsQualifyingInfrastructureCompanies =
    getEbitdaAdjustmentsQualifyingInfrastructureCompanies(tooltips);
  const ebitdaOther = getEbitdaOther(tooltips);

  return [
    ...getGroupDataInitialSheetBuilder(),
    {
      label: `NGIE`,
      type: "header",
      value: getSelectedPeriod()?.local_curr_net_group_interest_expense ?? 0,
      validation: "number",
    },
    { label: "" },
    { label: `Group's Profit Before Tax`, type: "header" },
    ...getTableRows(ebitdaProfitBeforeTax, isImportTemplate),
    {
      label:
        "Depreciation and Amortisation adjustment - Capital Expenditure Adjustment (s417)",
      type: "header",
    },
    ...getTableRows(
      ebitdaDepreciationAndAmortisationAdjustment,
      isImportTemplate,
    ),
    { label: "Capital fair value movement adjustment (s418):", type: "header" },
    ...getTableRows(ebitdaCapitalFairValueMovementAdjustment, isImportTemplate),
    { label: "Capital disposals adjustment (s419):", type: "header" },
    ...getTableRows(ebitdaCapitalDisposalsAdjustment, isImportTemplate),
    { label: "" },
    {
      label: "Other adjustments required to calculate NGIE",
      type: "header",
      headerType: "h1",
    },
    {
      label:
        "Adjustments in respect of the interest allowance (alternative calculation) election (s423-426):",
      type: "header",
    },
    ...getTableRows(
      ebitdaAdjustentsInRespectOfTheInterestAllowanceAlternativeCalculation,
      isImportTemplate,
    ),
    {
      label:
        "Adjustments in respect of the interest allowance (non-consolidated investment) election.",
      type: "header",
    },
    ...getTableRows(ebitdaAdjustmentsNonConsolidated, isImportTemplate),
    {
      label:
        "Adjustments in respect of the interest allowance (consolidated partnership) election.",
      type: "header",
    },
    ...getTableRows(ebitdaAdjustmentsConsolidatedPartnership, isImportTemplate),
    {
      label: "Adjustments in respect of qualifying infrastructure companies",
      type: "header",
    },
    ...getTableRows(
      ebitdaAdjustmentsQualifyingInfrastructureCompanies,
      isImportTemplate,
    ),
    { label: "Other", type: "header" },
    ...getTableRows(ebitdaOther, isImportTemplate),
  ];
};

const worksheetName = "Group-EBITDA";

export async function handleEbitdaTemplateDownload({
  workbook,
  isImportTemplate = true,
  isClientFacing = true,
}) {
  const params = {
    sheetBuilder: generateSheetBuilder(isImportTemplate),
    documentName: "Group-EBITDA Import Template",
    worksheetName,
    isClientFacing,
  };
  if (workbook) {
    addGroupDataWorksheet({ ...params, workbook, isImportTemplate });
  } else {
    await downloadGroupDataImportTemplate(params);
  }
}

export async function handleEbitdaImport({
  file,
  setErrors,
  setNewData,
  setWarnings,
}) {
  try {
    const worksheet = await getWorksheetFromFile(file, worksheetName);
    const sheetBuilder = generateSheetBuilder();
    const { results: ebitda, warnings } = await extractKeyValuePairsFromExcel(
      worksheet,
      sheetBuilder,
    );

    // validate ebitda keys
    validateImportedGroupData({
      importedData: ebitda,
      sheetBuilder,
      setErrors,
    });

    if (warnings?.length > 0) {
      setNewData(ebitda);
      setWarnings(warnings);
      return;
    }

    return ebitda;
  } catch (error) {
    const errorObject = JSON.parse(error.message);
    console.error("Error importing EBITDA:", errorObject ?? error);
    if (errorObject.missingWorkSheetErrorMessage) {
      setErrors([
        {
          title:
            errorObject.missingWorkSheetErrorMessage ??
            "Error Importing EBITDA",
        },
      ]);
    }
  }
}
